/**
 * Consent state for analytics and advertising storage.
 *
 * The decision lives in three places and they must agree:
 *
 *   1. `localStorage['localingos.consent']` — the durable record.
 *   2. `data-consent` on <html> — resolved before the first paint by the inline
 *      script in public/index.html, and the only thing that decides whether the
 *      consent banner is visible (see ConsentBanner.css).
 *   3. Google Consent Mode / PostHog's opt-in flag — the actual enforcement.
 *
 * Read from (2) rather than (1) for the same reason ThemeContext does: marketing
 * pages are prerendered and hydrated, so React's first render has to describe the
 * document that is already on screen, not re-derive it. localStorage is only
 * consulted as a fallback for contexts where the pre-paint script never ran
 * (the dev server's first load with a hand-edited index.html, and unit tests).
 */

import { updateGtagConsent } from './analytics';
import { setPostHogConsent } from './posthog';

/** localStorage key holding the durable decision. Mirrored in public/index.html. */
export const CONSENT_STORAGE_KEY = 'localingos.consent';

/** Attribute on <html> that drives the banner's visibility. Mirrored in public/index.html. */
export const CONSENT_ATTRIBUTE = 'data-consent';

/** An explicit choice the visitor has made. */
export type ConsentDecision = 'granted' | 'denied';

/** A decision, or the absence of one. */
export type ConsentState = ConsentDecision | 'unset';

function isDecision(value: unknown): value is ConsentDecision {
    return value === 'granted' || value === 'denied';
}

function readStoredDecision(): ConsentState {
    try {
        const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
        return isDecision(stored) ? stored : 'unset';
    } catch {
        // localStorage unavailable (private browsing, blocked cookies). Without a
        // durable record we cannot claim consent, so the visitor stays undecided.
        return 'unset';
    }
}

/**
 * The visitor's current decision.
 *
 * Safe to call during the prerender pass and during render: it touches no
 * network, and returns 'unset' when there is no document (so a prerendered
 * snapshot always contains the banner, which the pre-paint script then hides for
 * visitors who have already decided).
 */
export function getConsentState(): ConsentState {
    if (typeof document === 'undefined') return 'unset';

    const attribute = document.documentElement.getAttribute(CONSENT_ATTRIBUTE);
    if (isDecision(attribute)) return attribute;
    if (attribute === 'unset') return 'unset';

    return readStoredDecision();
}

/** Whether analytics and advertising storage may be used. */
export function isConsentGranted(): boolean {
    return getConsentState() === 'granted';
}

/**
 * Records a decision and applies it everywhere.
 *
 * Persist first, then paint, then enforce: if writing the attribute throws we
 * still want the durable record, and if enforcement throws we still want the
 * banner gone rather than a button that appears to do nothing.
 */
export function setConsent(decision: ConsentDecision): void {
    try {
        localStorage.setItem(CONSENT_STORAGE_KEY, decision);
    } catch {
        // Not persistable — the choice still applies for this page view, the
        // visitor will just be asked again next time. Better than ignoring them.
    }

    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute(CONSENT_ATTRIBUTE, decision);
    }

    const granted = decision === 'granted';
    updateGtagConsent(granted);
    setPostHogConsent(granted);
}
